import { useEffect, useMemo } from 'react';
import { AlertTriangle, Warehouse } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { Card } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { EmptyState } from '../../../components/EmptyState';
import { useAssetStore } from '../../../store/assetStore';
import { useAssetCatalogStore } from '../../../store/assetCatalogStore';
import { useAssetSettingsStore } from '../../../store/assetSettingsStore';

/**
 * Stock by category, computed from assets.assets. A category is "low" when
 * its in-stock count is at or below the low-stock threshold saved in
 * Asset settings (assets.asset_settings.stock.low_stock_threshold).
 */
export function StockManagementView() {
  const { assets, fetchAssets, isLoading } = useAssetStore();
  const { categories, fetchCategories } = useAssetCatalogStore();
  const { settings, fetchSettings } = useAssetSettingsStore();

  useEffect(() => {
    fetchAssets();
    fetchCategories();
    fetchSettings();
  }, [fetchAssets, fetchCategories, fetchSettings]);

  const threshold = settings?.stock.low_stock_threshold ?? null;
  const active = assets.filter((a) => a.status !== 'Retired' && a.status !== 'Disposed');
  const inStock = active.filter((a) => a.status === 'In Stock');

  const rows = useMemo(() => {
    const names = new Set<string>([...categories.map((c) => c.name), ...active.map((a) => a.category || 'Uncategorised')]);
    return [...names].map((name) => {
      const inCat = active.filter((a) => (a.category || 'Uncategorised') === name);
      const stock = inCat.filter((a) => a.status === 'In Stock').length;
      return {
        name,
        total: inCat.length,
        inStock: stock,
        assigned: inCat.filter((a) => a.status === 'Assigned').length,
        maintenance: inCat.filter((a) => a.status === 'Under Maintenance').length,
        low: threshold !== null && inCat.length > 0 && stock <= threshold,
      };
    }).sort((a, b) => Number(b.low) - Number(a.low) || b.total - a.total || a.name.localeCompare(b.name));
  }, [categories, active, threshold]);

  const lowCount = rows.filter((r) => r.low).length;

  return (
    <div className="space-y-4 p-4">
      <PageHeader className="mb-0" icon={Warehouse} title="Stock management" description="In-stock devices per category and low-stock alerts." />

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">In stock</p>
          <p className="mt-2 font-display text-metric text-foreground">{inStock.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Distinct models in stock</p>
          <p className="mt-2 font-display text-metric text-foreground">{new Set(inStock.map((a) => a.model).filter(Boolean)).size}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Low-stock categories</p>
          <p className={`mt-2 font-display text-metric ${lowCount ? 'text-warning-strong' : 'text-foreground'}`}>{lowCount}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {threshold === null ? 'Loading threshold…' : `At or below ${threshold} in stock (set in Asset settings)`}
          </p>
        </Card>
      </div>

      <Card>
        <div className="border-b border-border px-4 py-3">
          <h2 className="flex items-center gap-2 font-display text-card-title text-foreground"><Warehouse className="size-4" /> Stock by category</h2>
        </div>
        {rows.length === 0 ? (
          <EmptyState className="m-4 border-0" title={isLoading ? 'Loading…' : 'No assets registered yet'} description="Register assets or receive a purchase order to build stock." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-semibold">Category</th>
                  <th className="px-4 py-2 text-right font-semibold">In stock</th>
                  <th className="px-4 py-2 text-right font-semibold">Assigned</th>
                  <th className="px-4 py-2 text-right font-semibold">In maintenance</th>
                  <th className="px-4 py-2 text-right font-semibold">Total</th>
                  <th className="px-4 py-2 font-semibold" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => (
                  <tr key={r.name} className="hover:bg-secondary/30">
                    <td className="px-4 py-2 text-foreground">{r.name}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-foreground">{r.inStock}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{r.assigned}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{r.maintenance}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{r.total}</td>
                    <td className="px-4 py-2 text-right">
                      {r.low && <Badge variant="warning"><AlertTriangle className="size-3" /> Low stock</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
